import type { FindingDefinitionDto, PerformanceMeasureDefinitionDto } from "@repo/data-store";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@repo/ui/components/ui/command";
import { type MouseEvent, type ReactNode, useState } from "react";
import { markdownLine } from "@/shared/lib/markdown-line";
import type { ExamOption } from "../lib/exam-options";

/** What ordering a procedure needs from a definition: the pin, its default length, and a label. */
export interface ProcedureOrderOption {
  definitionUrl: string;
  name: string;
  description?: string;
  version: number;
  /** Which catalog it came from — what a draft order records before the saved set brings its declarations. */
  kind: "treatment" | "diagnostic";
}

export interface OrderPickerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  procedures: readonly ProcedureOrderOption[];
  onSelectProcedure: (option: ProcedureOrderOption) => void;
  exams?: readonly ExamOption[];
  onSelectExam?: (option: ExamOption) => void;
  findings?: readonly FindingDefinitionDto[];
  onSelectFinding?: (option: FindingDefinitionDto) => void;
  performanceMeasures?: readonly PerformanceMeasureDefinitionDto[];
  onSelectPerformanceMeasure?: (option: PerformanceMeasureDefinitionDto) => void;
  /** Names where a procedure will land, so the picker says what the timeline's selection means. */
  target?: string;
  disabled?: boolean;
}

/**
 * Keeps focus in the search when the list is pressed. cmdk's listbox is
 * `tabIndex={-1}`, and a disabled row is `pointer-events: none` in the shared
 * item styles, so pressing one delivers the mousedown to the listbox, which
 * then takes focus. Typing went nowhere, and Enter, still handled by the root,
 * picked whatever row was highlighted: an immediate `addOrder` on an unbooked
 * set. It sits on the list, not the rows, because a disabled row never
 * receives the event. The click still selects an enabled row.
 */
function keepSearchFocused(event: MouseEvent) {
  event.preventDefault();
}

/**
 * Catalog picker for procedure work, patient exams, and visit findings.
 *
 * Grouped only while nothing is typed. cmdk 1.1.1 ranks items within a group
 * but never reorders the groups themselves — it looks them up by id, and the
 * attribute it reads holds the heading — so a search ranked every procedure
 * above the best-matching exam. Searching drops the groups, and cmdk's item
 * sort then ranks across kinds. Unsearched, the few exams lead, since the
 * procedures are many.
 *
 * Items search on name only (`value`, which cmdk scores together with any
 * `keywords`). The canonical url used to ride along, and its uuid letters gave
 * unrelated procedures a fuzzy match on almost any query.
 */
export function OrderPickerDialog({
  open,
  onOpenChange,
  procedures,
  onSelectProcedure,
  exams = [],
  onSelectExam,
  findings = [],
  onSelectFinding,
  performanceMeasures = [],
  onSelectPerformanceMeasure,
  target,
  disabled = false,
}: OrderPickerDialogProps) {
  const [search, setSearch] = useState("");
  const change = (next: boolean) => {
    if (!next) setSearch("");
    onOpenChange(next);
  };
  const examItems = onSelectExam
    ? exams.map((option) => (
        <CommandItem
          key={`exam:${option.definitionUrl}`}
          value={option.name}
          disabled={disabled}
          onSelect={() => {
            if (disabled) return;
            change(false);
            onSelectExam(option);
          }}
        >
          <div className="flex min-w-0 flex-1 flex-col">
            <span className="text-sm font-medium">{option.name}</span>
            {option.description && (
              <span className="text-xs text-muted-foreground line-clamp-1">{markdownLine(option.description)}</span>
            )}
          </div>
          <span className="text-xs text-muted-foreground">Exam</span>
        </CommandItem>
      ))
    : [];
  const findingItems = onSelectFinding
    ? findings.map((option) => (
        <CommandItem
          key={`finding:${option.definitionUrl}`}
          value={option.name}
          disabled={disabled}
          onSelect={() => {
            if (disabled) return;
            change(false);
            onSelectFinding(option);
          }}
        >
          <div className="flex min-w-0 flex-1 flex-col">
            <span className="text-sm font-medium">{option.name}</span>
            {option.description && (
              <span className="text-xs text-muted-foreground line-clamp-1">{markdownLine(option.description)}</span>
            )}
          </div>
          <span className="text-xs text-muted-foreground">Finding</span>
        </CommandItem>
      ))
    : [];
  const performanceItems = onSelectPerformanceMeasure
    ? performanceMeasures.map((option) => (
        <CommandItem
          key={`performance:${option.url}`}
          value={option.name}
          disabled={disabled}
          onSelect={() => {
            if (disabled) return;
            change(false);
            onSelectPerformanceMeasure(option);
          }}
        >
          <div className="flex min-w-0 flex-1 flex-col">
            <span className="text-sm font-medium">{option.name}</span>
            {option.description && (
              <span className="text-xs text-muted-foreground line-clamp-1">{markdownLine(option.description)}</span>
            )}
          </div>
          <span className="text-xs text-muted-foreground">Performance measure</span>
        </CommandItem>
      ))
    : [];
  const procedureItems = procedures.map((option) => (
    <CommandItem
      key={`procedure:${option.definitionUrl}`}
      value={option.name}
      disabled={disabled}
      onSelect={() => {
        if (disabled) return;
        change(false);
        onSelectProcedure(option);
      }}
    >
      <div className="flex min-w-0 flex-1 flex-col">
        <span className="text-sm font-medium">{option.name}</span>
        {option.description && (
          <span className="text-xs text-muted-foreground line-clamp-1">{markdownLine(option.description)}</span>
        )}
      </div>
    </CommandItem>
  ));
  const group = (heading: string, items: ReactNode[]) =>
    items.length > 0 && <CommandGroup heading={heading}>{items}</CommandGroup>;
  return (
    <CommandDialog
      open={open}
      onOpenChange={change}
      className="sm:max-w-lg"
      title={
        procedures.length
          ? "Add procedure"
          : onSelectFinding || onSelectPerformanceMeasure
            ? "Add assessment"
            : "Add exam"
      }
      description={target ?? "Search the catalog"}
    >
      <CommandInput
        placeholder={
          procedures.length
            ? "Search procedures…"
            : onSelectFinding || onSelectPerformanceMeasure
              ? "Search assessments…"
              : "Search exams…"
        }
        value={search}
        onValueChange={setSearch}
      />
      <CommandList onMouseDown={keepSearchFocused}>
        <CommandEmpty>Nothing available.</CommandEmpty>
        {search ? (
          [...examItems, ...findingItems, ...performanceItems, ...procedureItems]
        ) : (
          <>
            {group("Exams", examItems)}
            {group("Findings", findingItems)}
            {group("Performance measures", performanceItems)}
            {group("Procedures", procedureItems)}
          </>
        )}
      </CommandList>
    </CommandDialog>
  );
}
